<template>
    <div class="search-form">
        <div v-if="previewMode" class="search-form__preview">
            <div class="search-form__preview__location" @click="activateInput('locationFilter')">
                <svg-icon name="location" />
                <span>{{ locationPreviewLabel }}</span>
            </div>
            <div class="search-form__preview__dates" @click="activateInput('datesFilter')">
                <svg-icon name="datepicker" />
                <span>{{ datesPreviewLabel }}</span>
            </div>
        </div>

        <template v-else>
            <MobileSearchFormPopup
                v-if="!isLargeScreen"
                :location-label="locationPreviewLabel"
                :dates-label="datesPreviewLabel"
                :dates-step-active="datesStepActive"
                @close="handleMobilePopupClose"
                @search="applyNewFilters"
            />
            <div v-else class="search-form__active-state">
                <div class="search-form__element">
                    <span class="label">{{ $t('search_form.location_label') }}</span>
                    <LocationFilter
                        ref="locationFilter"
                        class="search-form__input"
                        @location-change="handleLocationChange"
                    />
                </div>

                <div class="search-form__element">
                    <span class="label">{{ $t('search_form.dates_label') }}</span>
                    <VehicleDatesSearch
                        ref="datesFilter"
                        :placeholder="$t('search_form.dates_placeholder')"
                        class="search-form__input search-form__date"
                        @input="focusSearchButton"
                    />
                </div>
            </div>
        </template>

        <button
            v-if="!previewMode"
            ref="searchBtn"
            class="button button--primary search-form__search"
            @click="applyNewFilters"
        >
            {{ $t('search_form.button') }}
        </button>

        <div class="search-form__more-filters">
            <button class="button button--secondary-contrast" @click="displayAdditionalFiltersModal">
                <svg-icon class="button__icon" name="preference" />
                <span>{{ $t('search_form.show_filters') }}</span>
            </button>
            <Badge
                v-if="additionalFiltersCount > 0"
                :count="additionalFiltersCount"
                class="search-form__more-filters__badge"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { actionTypes as filterActionTypes } from '~/store/filters';
import modalMixin from '~/mixins/modalMixin';
import {
    buildQuery,
    getFilterValue,
    getDefaultFilterValue,
    getActiveAdditionalFiltersCount,
} from '~/helpers/queryParser';
import { PLACE_DESCRIPTION, AVAILABLE_BETWEEN } from '~/config/filterKeys';

import VehicleDatesSearch from '~/components/vehicles/VehicleDatesSearch.vue';
import LocationFilter from '~/components/vehicles/LocationFilter.vue';
import Badge from '~/components/Badge.vue';
import MobileSearchFormPopup from '~/components/vehicles/MobileSearchFormPopup.vue';
import { isGeolocatedKey } from '~/config/filters';

export default {
    components: {
        LocationFilter,
        VehicleDatesSearch,
        Badge,
        MobileSearchFormPopup,
    },
    mixins: [modalMixin],
    props: {
        previewMode: {
            type: Boolean,
            default: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        filtersOpen: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            datesStepActive: false,
        };
    },
    computed: {
        ...mapGetters(['standByFilters', 'isLargeScreen']),
        additionalFiltersCount() {
            return getActiveAdditionalFiltersCount(this.$route.query);
        },
        locationPreviewLabel() {
            const value =
                this.standByFilters[PLACE_DESCRIPTION] !== undefined
                    ? this.standByFilters[PLACE_DESCRIPTION]
                    : getFilterValue(this.$route.query, PLACE_DESCRIPTION);

            if (value === getDefaultFilterValue(PLACE_DESCRIPTION)) {
                return this.$t('search_form.location');
            }

            if (value === isGeolocatedKey) {
                return this.$t('search_form.location_near_me');
            }

            if (Array.isArray(value)) {
                return value.join(', ');
            }

            return value;
        },
        datesPreviewLabel() {
            const dates =
                this.standByFilters[AVAILABLE_BETWEEN] !== undefined
                    ? this.standByFilters[AVAILABLE_BETWEEN]
                    : getFilterValue(this.$route.query, AVAILABLE_BETWEEN);

            if (JSON.stringify(dates) === JSON.stringify(getDefaultFilterValue(AVAILABLE_BETWEEN))) {
                return this.$t('search_form.dates');
            }

            return `${this.$options.filters.dateFormat(dates.from)} - ${this.$options.filters.dateFormat(dates.to)}`;
        },
    },
    methods: {
        activateInput(filter) {
            this.datesStepActive = false;
            this.$emit('turn-on-active-mode', filter);

            if (window.innerWidth < 991) {
                if (filter === 'datesFilter') {
                    this.datesStepActive = true;
                }
            } else {
                this.$nextTick(() => {
                    switch (filter) {
                        case 'locationFilter':
                            this.$refs.locationFilter.focusInput();
                            break;
                        case 'datesFilter':
                            this.$root.$emit('focus-datepicker');
                            break;
                        default:
                            break;
                    }
                });
            }
        },
        focusSearchButton() {
            if (this.isLargeScreen) {
                this.$refs.searchBtn?.focus();
            }
        },
        handleLocationChange(event) {
            if (event) {
                this.activateInput('datesFilter');
            }
        },
        applyNewFilters() {
            const query = buildQuery(this.$route.query, this.standByFilters);
            this.$router.push({ query });
            this.$root.$emit('turn-on-preview-mode');
        },
        clearMainStandByFilters() {
            this.$store.dispatch(filterActionTypes.CLEAR_MAIN_STAND_BY_FILTERS).catch(err => {
                this.$sentry.captureException(err);
            });
        },
        handleMobilePopupClose() {
            this.$root.$emit('turn-on-preview-mode');
            this.clearMainStandByFilters();
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.search-form {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    @include font-size(16px);

    @include lg {
        align-items: flex-end;
        flex-wrap: wrap;
        gap: 24px;
        max-width: 1250px;
    }

    &__element {
        margin-top: 0;
        min-width: 200px;
        width: 296px;
    }

    &__preview {
        display: flex;
        gap: 16px;
        height: 48px;
        width: 100%;
        padding: 8px 20px;
        border: 1px solid $gray;
        border-radius: $default-border-radius;
        box-sizing: border-box;
        min-width: 0;
        @include font-size(14px);
        color: $darkest-gray;

        &__location,
        &__dates {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 4px;
            flex: 1;
            cursor: pointer;
            white-space: nowrap;
            min-width: 0;

            span {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        &__location {
            border-right: 1px solid $gray;
            padding-right: 16px;
        }

        .svg-icon {
            display: none;
            width: 24px;
            min-width: 24px;
            height: 24px;
        }

        @include sm {
            width: auto;
            min-width: 500px;
        }

        @include lg {
            width: 514px;
            padding: 12px 20px;
            gap: 24px;
            @include font-size(16px);
            color: $primary;

            .svg-icon {
                display: block;
            }

            &__location {
                padding-right: 24px;
            }

            &__location,
            &__dates {
                justify-content: flex-start;
            }
        }
    }

    &__active-state {
        display: none;

        @include lg {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            min-width: 600px;
        }
    }

    &__more-filters {
        position: relative;

        button {
            padding: 8px;
        }

        .svg-icon {
            color: $primary;
            margin: 0;
        }

        span {
            display: none;
        }

        &__badge {
            position: absolute;
            top: -8px;
            right: -8px;
        }

        @include lg {
            button {
                padding: 10px 24px;
            }

            .svg-icon {
                margin-left: 5px;
            }

            span {
                display: block;
            }
        }
    }

    &__search {
        padding: 12px 24px;
    }

    &__element {
        margin-top: 10px;

        &:first-child {
            margin-top: 0;
        }

        @include lg {
            margin-top: 0;
        }
    }
}
</style>
